import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, ArrowUpRight } from "lucide-react";
import { Monogram } from "@/components/Logo";
import { CONTACT } from "@/data/products";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-espresso text-cream/80">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pt-20 pb-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pb-16 border-b border-white/10">
          <div className="max-w-xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-champagne mb-6">Begin something beautiful</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] text-cream">
              Let's create something <span className="italic text-champagne">they'll never forget.</span>
            </h2>
          </div>
          <Link
            to="/gift-edit#brief"
            data-testid="footer-cta-gift-edit"
            className="group inline-flex items-center gap-2 bg-gold text-espresso px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-champagne transition-colors duration-300 shrink-0"
          >
            Create a Gift Edit
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-14">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <Monogram size={36} dark />
              <span className="font-serif text-lg font-semibold text-cream">Salem &amp; Sabrina</span>
            </div>
            <p className="text-sm leading-relaxed text-cream/60">
              Personalised gifting, a ready-to-gift collection and a private shopping concierge — every parcel wrapped with intention.
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne mb-5">Explore</p>
            <ul className="space-y-3 text-sm">
              <li><Link data-testid="footer-link-gift-edit" className="hover:text-champagne transition-colors" to="/gift-edit">The Gift Edit</Link></li>
              <li><Link data-testid="footer-link-collection" className="hover:text-champagne transition-colors" to="/collection">The Collection</Link></li>
              <li><Link data-testid="footer-link-concierge" className="hover:text-champagne transition-colors" to="/concierge">Private Concierge</Link></li>
              <li><Link data-testid="footer-link-gallery" className="hover:text-champagne transition-colors" to="/gallery">Gallery</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne mb-5">The House</p>
            <ul className="space-y-3 text-sm">
              <li><Link data-testid="footer-link-about" className="hover:text-champagne transition-colors" to="/about">Our Story</Link></li>
              <li><Link data-testid="footer-link-faqs" className="hover:text-champagne transition-colors" to="/faqs">FAQs</Link></li>
              <li><Link data-testid="footer-link-contact" className="hover:text-champagne transition-colors" to="/faqs#contact">Contact Us</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne mb-5">Contact</p>
            <ul className="space-y-3 text-sm">
              <li>
                <a data-testid="footer-email-link" href={`mailto:${CONTACT.email}`} className="hover:text-champagne transition-colors break-all">
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a data-testid="footer-phone-link" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hover:text-champagne transition-colors">
                  {CONTACT.phone}
                </a>
              </li>
              <li className="text-cream/50">{CONTACT.hours}</li>
            </ul>
            <div className="flex gap-3 mt-6">
              <a data-testid="social-instagram" href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-gold hover:text-champagne transition-colors">
                <Instagram size={15} />
              </a>
              <a data-testid="social-facebook" href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-gold hover:text-champagne transition-colors">
                <Facebook size={15} />
              </a>
              <a data-testid="social-email" href={`mailto:${CONTACT.email}`} aria-label="Email"
                className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-gold hover:text-champagne transition-colors">
                <Mail size={15} />
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">
            © {new Date().getFullYear()} Salem &amp; Sabrina — The Gift Edit
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">Bespoke Gifting · Private Concierge</p>
        </div>
      </div>
    </footer>
  );
}
